import React, {useContext, useState, useEffect} from 'react';
import ReactDOM from 'react-dom';
import {Trans, useTranslation} from "react-i18next";
import PlanName from "./PlanName.jsx";
import {DateTimeFormatter, LocalDateTime} from "@js-joda/core";
import {SubscriptionApi} from "../../../api/authority/generated/index.ts";
import {GlobalContext} from "../../../utils/context/GlobalContextProvider.jsx";

const ConfirmationModal = ({ isOpen, onConfirm, onClose, project, packageInfo, isAnnual, onBillingCycleChange, currencyCode = 'CZK' }) => {
    const { getAuthorityConfiguration } = useContext(GlobalContext);
    const { t } = useTranslation();
    const [priceQuote, setPriceQuote] = useState(null);
    const [priceQuoteError, setPriceQuoteError] = useState(false);

    const handleBillingCycleChange = (nextIsAnnual) => {
        setPriceQuote(null);
        setPriceQuoteError(false);
        onBillingCycleChange(nextIsAnnual);
    };

    let type = 'new';
    let expiresString = null;
    if (packageInfo && project && project.subscription) {
        const subscription = project.subscription;
        const expires = LocalDateTime.parse(subscription.endDate);
        expiresString = expires.format(DateTimeFormatter.ofPattern('dd.MM.yyyy'));
        const currentCycle = subscription.billingCycle;
        const currencySuffix = currencyCode === 'EUR' ? 'Eur' : 'Czk';
        const currentPrice = subscription.currentPackage[`${currentCycle === 'annual' ? 'yearly' : 'monthly'}${currencySuffix}`];
        const selectedPrice = packageInfo[`${isAnnual ? 'yearly' : 'monthly'}${currencySuffix}`];
        const isFreeToPaid = currentPrice === 0 && selectedPrice > 0;

        if (subscription.isFreeTrial && !isFreeToPaid) {
            type = 'free';
        } else if (isFreeToPaid) {
            type = 'upgrade';
        } else {
            type = currentCycle === 'annual' && !isAnnual
                ? 'downgrade'
                : currentCycle === 'monthly' && isAnnual
                    ? 'upgrade'
                    : currentPrice > selectedPrice ? 'downgrade' : 'upgrade';
        }
    }

    useEffect(() => {
        let active = true;
        setPriceQuote(null);
        setPriceQuoteError(false);
        if (!isOpen || !project?.id || !packageInfo?.id) {
            return () => { active = false; };
        }

        const api = new SubscriptionApi(getAuthorityConfiguration());
        api.getSubscriptionChangePrice({
            projectId: project.id,
            packageId: packageInfo.id,
            billingCycle: isAnnual ? 'annual' : 'monthly',
        })
            .then((quote) => {
                if (active) setPriceQuote(quote);
            })
            .catch(error => {
                if (!active) return;
                setPriceQuoteError(true);
                console.error("Failed to fetch subscription price quote:", error);
            });

        return () => { active = false; };
    }, [isOpen, getAuthorityConfiguration, project?.id, packageInfo?.id, isAnnual]);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;
    const currencySymbol = currencyCode === 'EUR' ? '€' : 'Kč';
    const netAmount = (priceQuote?.netAmount ?? 0) / 100;
    const vatAmount = (priceQuote?.vatAmount ?? 0) / 100;
    const roundingAmount = (priceQuote?.roundingAmount ?? 0) / 100;
    const grossAmount = (priceQuote?.totalAmount ?? 0) / 100;
    const prorationAmount = (priceQuote?.amount ?? 0) / 100;
    const renderAmount = (amount, forceCents = false) => amount.toLocaleString('cs-CZ', {
        minimumFractionDigits: forceCents ? 2 : 0,
        maximumFractionDigits: 2,
    });

    return ReactDOM.createPortal(
        <div className="modal__billing fixed inset-0 z-50 flex overflow-auto bg-black/50">
            <div
                className="relative bg-white dark:bg-gray-800 w-full max-w-lg m-auto flex-col flex rounded-lg shadow-lg">
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700/60">
                    <div className="flex justify-between items-center">
                        <div className="font-semibold text-gray-800 dark:text-gray-100">
                            {t('subscription.package-change-title')}
                        </div>
                        <button
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                            onClick={onClose}
                        >
                            <div className="sr-only">Close</div>
                            <svg className="fill-current" width="16" height="16" viewBox="0 0 16 16">
                                <path
                                    d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    <div className="mb-4 flex items-center justify-end gap-3">
                        <span className={`text-sm ${!isAnnual ? 'font-bold text-gray-800' : 'font-medium text-gray-500'}`}>
                            {t('payment.monthly')}
                        </span>
                        <div className="form-switch">
                            <input
                                type="checkbox"
                                id="modal-billing-cycle-toggle"
                                className="sr-only"
                                checked={isAnnual}
                                onChange={(event) => handleBillingCycleChange(event.target.checked)}
                            />
                            <label className="bg-gray-400 dark:bg-gray-700" htmlFor="modal-billing-cycle-toggle">
                                <span className="bg-white shadow-sm" aria-hidden="true"></span>
                                <span className="sr-only">{t('payment.pay-annually')}</span>
                            </label>
                        </div>
                        <span className={`text-sm ${isAnnual ? 'font-bold text-gray-800' : 'font-medium text-gray-500'}`}>
                            {t('payment.annually')}
                        </span>
                    </div>

                    <div className="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {t('subscription.confirm-message')}
                    </div>

                    {priceQuote ? <div
                        className="mb-3 w-full h-full text-left py-3 px-4 rounded-lg bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-500 shadow-sm"
                    >
                        <div className="mb-3 text-base font-semibold text-gray-800 dark:text-gray-100">
                            <PlanName planCode={packageInfo.code}/>
                        </div>
                        <div className="space-y-2 border-t border-gray-200 pt-3 dark:border-gray-700">
                            <div className="flex items-baseline justify-between gap-4">
                                <span className="text-sm text-gray-600 dark:text-gray-300">
                                    {t('subscription.price-excluding-vat')}
                                </span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">
                                    {renderAmount(netAmount)} {currencySymbol}
                                </span>
                            </div>
                            <div className="flex items-baseline justify-between gap-4">
                                <span className="text-sm text-gray-600 dark:text-gray-300">
                                    {priceQuote.reverseCharge
                                        ? t('subscription.vat-reverse-charge')
                                        : t('subscription.vat-rate', {rate: priceQuote.vatPercentage})}
                                </span>
                                <span className="font-medium text-gray-800 dark:text-gray-100">
                                    {renderAmount(vatAmount)} {currencySymbol}
                                </span>
                            </div>
                            {roundingAmount !== 0 && (
                                <div className="flex items-baseline justify-between gap-4">
                                    <span className="text-sm text-gray-600 dark:text-gray-300">{t('subscription.rounding')}</span>
                                    <span className="font-medium text-gray-800 dark:text-gray-100">
                                        {roundingAmount > 0 ? '+' : ''}{renderAmount(roundingAmount, true)} {currencySymbol}
                                    </span>
                                </div>
                            )}
                            <div className="flex items-baseline justify-between gap-4 border-t border-gray-200 pt-3 dark:border-gray-700">
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                    {t('subscription.full-period-total', {defaultValue: 'Celkem za fakturační období'})}
                                </span>
                                <div className="text-lg font-bold text-green-600">
                                    {renderAmount(grossAmount)} {currencySymbol}
                                </div>
                            </div>
                        </div>
                    </div> : (
                        <div className={`mb-3 rounded-lg border px-4 py-4 text-sm ${priceQuoteError
                            ? 'border-red-300 bg-red-50 text-red-700'
                            : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                            {priceQuoteError ? t('subscription.price-quote-failed') : t('subscription.price-quote-loading')}
                        </div>
                    )}
                    {type === 'upgrade' && prorationAmount > 0 && (
                        <div className="mb-3 overflow-hidden rounded-lg border-2 border-gray-800 bg-white shadow-sm dark:border-gray-200 dark:bg-gray-800">
                            <div className="flex items-baseline justify-between gap-4 bg-gray-900 px-4 py-3 text-white dark:bg-gray-100 dark:text-gray-900">
                                <span className="text-sm font-semibold">
                                    {t('subscription.due-now-final', {defaultValue: 'K úhradě nyní – konečná cena'})}
                                </span>
                                <div className="text-lg font-bold">
                                    {renderAmount(prorationAmount)} {currencySymbol}
                                </div>
                            </div>
                            <div className="px-4 py-3 text-xs leading-5 text-gray-700 dark:text-gray-200">
                                <Trans
                                    i18nKey="subscription.upgrade-active"
                                    values={{expires: expiresString}}
                                    components={{strong: <strong/>}}
                                />
                            </div>
                        </div>
                    )}
                    {!project.subscription && (
                        <div className="mb-3 rounded-lg bg-violet-50 px-4 py-3 text-xs leading-5 text-violet-900 dark:bg-violet-500/10 dark:text-violet-200">
                            <Trans
                                i18nKey="subscription.trial-payment-note"
                                values={{
                                    amount: renderAmount(grossAmount),
                                    currency: currencySymbol,
                                }}
                                components={{strong: <strong/>}}
                            />
                        </div>
                    )}
                    {expiresString && (
                        <div className={'text-xs text-gray-600'}>
                            {type === 'free' && (
                                <span>
                                    <Trans
                                        i18nKey="subscription.free-active"
                                        values={{expires: expiresString}}
                                        components={{strong: <strong/>}}
                                    />
                                </span>
                            )}
                            {type === 'downgrade' && (
                                <span>
                                    <Trans
                                        i18nKey="subscription.downgrade-active"
                                        values={{expires: expiresString}}
                                        components={{strong: <strong/>}}
                                    />
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="px-5 py-2 flex justify-end border-t border-gray-200 dark:border-gray-700/60">
                    <button
                        className="btn bg-indigo-500 text-white"
                        onClick={onConfirm}
                        disabled={!priceQuote || priceQuoteError}
                    >
                        {!project.subscription
                            ? t('subscription.try-14-days-free')
                            : type === 'upgrade' && prorationAmount > 0
                                ? t('subscription.confirm-and-pay', {
                                    amount: renderAmount(prorationAmount),
                                    currency: currencySymbol,
                                })
                                : t('subscription.confirm')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConfirmationModal;
